import { useEffect, useRef, useState } from "react";

/**
 * Full-viewport WebGL scene: environment (human ↔ machine world) + registered
 * two-texture portrait composite, both driven by one ping-pong "energy" mask.
 * The mask is turned into soundwave bands so the robot is only glimpsed.
 * Writes pointer / parallax state into `fx` so other layers (AudioFX, DOM) can follow.
 */

export type DebugMode = "normal" | "opacity" | "split" | "overlay" | "mask";

export interface FluidRevealSettings {
  size: number; // brush radius multiplier
  yaw: number; pitch: number; pivotEase: number;
  seamJag?: number; seamGlow?: number;
  gStreams?: number; gSpeed?: number; gCurve?: number; // dark-world growth signals // 2.5D portrait pivot (0 = off)
  strength: number; // energy injected per frame
  decay: number; // persistence 0.85..0.995
  viscosity: number; // how much the field is dragged by the cursor
  velocity: number; // cursor speed sensitivity
  waves: number; // 0 = solid liquid reveal, 1 = soundwave-band reveal
  cap: number; // max robot visibility from the cursor (keeps the illusion)
  envBlend: number; // constant shift toward machine world
  xray: number; // X-ray line drawing strength (0 = off)
  xrayRate: number; // how often the X-ray pass sweeps
  envReact: number; // how much the environment responds to the cursor
  parallax: number;
  fullReveal: number; // 0..1 eased
  debug: DebugMode;
  mix: number;
  showEnv: boolean;
  freq: number; // waveform pulse frequency
  pinch: number; // 0 = deep pinch, 1 = no pinch
  glass: boolean; // static effect on/off
  refraction: number; // static strength
  split: number; // RGB split
  scan: number; // scan line density
  trailWaves: number; // cursor wave trail strength (0 = off)
  bgPulse: number; // background line breathing (0 = off)
  edgeAmp: number; // wave edge detail
  edgeMove: number; // wave edge movement
  // realism & bass polish (0 = off)
  shadow: number; grain: number; vignette: number; breath: number; light: number; soft: number;
  thump: number; quiet: number; chest: number;
  eyeFollow?: number; eyeGlow?: number; blinkRate?: number; sweep?: number; reflect?: number; // living eyes & chrome
  accent?: "blue" | "green" | "emerald";
  robotLines?: boolean; rlSpeed?: number; rlPulse?: number; rlBreath?: number; rlWake?: number; rlRange?: number; rlGlow?: number; rlCurrents?: number; rlNeon?: number; rlBloom?: number; rlContrast?: number; // living blue lines
  copper?: boolean; cuAmt?: number; cuSpeed?: number; cuHand?: number; spkAmt?: number; spkSize?: number; // copper + speakers
  pencil?: number; dust?: number; exitStyle?: "slide" | "iris";
  earGlow?: number; mouthMeter?: number; ink?: number; lamp?: number; humanBreathe?: number; // face + human side
  dSpec: number; dRib: number; dVinyl: number; dBloom: number; dDust: number; // dark-world music layers
}

export interface HeadFont { family: string; weight: number; tracking: number; scale: number }

export interface PortraitRect { x: number; y: number; w: number; h: number } // CSS px, top-left origin

export interface FxState {
  x: number; y: number; // CSS px
  v: number; // smoothed 0..1 speed
  energy?: number; // 0..1, falls toward stillness when the cursor rests
  thump?: number; // 0..1 slow bass envelope
  active: boolean;
  parX: number; parY: number; // -1..1 smoothed
  full: number;
  iris?: number; // iris-close exit progress 0..1
  seamY?: number; // how far the seam has grown up from the bottom (0..1)
  seam?: number; // split seam x (0..1 screen), >1 = off
}

interface Props {
  realSrc: string;
  robotSrc: string;
  maskSrc: string;
  robotPlaceholder?: boolean;
  aspect: number; // portrait image width / height
  rect: PortraitRect;
  settings: FluidRevealSettings;
  fx: React.MutableRefObject<FxState>;
  onReady?: () => void;
  lowPower?: boolean;
  headline: { left: string[]; right: string[] };
  onFail?: () => void;
  headFont: HeadFont;
  underline?: boolean;
  overlayRoot?: React.RefObject<HTMLElement>; // elements with [data-fx] are drawn into the text-effect layer
  redrawKey?: string;
}

const VERT = `attribute vec2 p;varying vec2 uv;void main(){uv=p*.5+.5;gl_Position=vec4(p,0.,1.);}`;

const NOISE = `
float h(vec2 p){return fract(sin(dot(p,vec2(127.1,311.7)))*43758.5453);}
float n(vec2 p){vec2 i=floor(p),f=fract(p);f=f*f*(3.-2.*f);return mix(mix(h(i),h(i+vec2(1,0)),f.x),mix(h(i+vec2(0,1)),h(i+1.),f.x),f.y);}
float fbm(vec2 p){float s=0.,a=.5;for(int i=0;i<4;i++){s+=a*n(p);p*=2.03;a*=.5;}return s;}`;

// Energy field: advected by curl-ish noise + dragged along cursor velocity, decays, splats a capsule.
const SIM = `precision highp float;varying vec2 uv;
uniform sampler2D prev;uniform vec2 a;uniform vec2 b;uniform vec2 vel;uniform float r;uniform float amt;
uniform float decay;uniform float visc;uniform float t;uniform float aspect;uniform float phase;uniform float freq;uniform float pinch;uniform float edgeAmp;uniform float edgeMove;${NOISE}
void main(){
  vec2 q=uv*3.+t*.08;
  float e=.01;
  float n1=fbm(q),nx=fbm(q+vec2(e,0.)),ny=fbm(q+vec2(0.,e));
  vec2 curl=vec2(ny-n1,-(nx-n1))/e*.0012;
  vec2 pa=(uv-a)*vec2(aspect,1.),ba=(b-a)*vec2(aspect,1.);
  float k=clamp(dot(pa,ba)/max(dot(ba,ba),1e-6),0.,1.);
  float d=length(pa-ba*k);
  float near=exp(-d*d/(r*r*8.));
  vec2 drag=vel*near*visc;
  float m=texture2D(prev,uv-curl-drag).r*decay;
  // irregular brush edge
  // waveform envelope: width swells & pinches with distance travelled along the path
  // round brush whose outline oscillates like a sound wave
  vec2 dv=pa-ba*k;
  float ang=atan(dv.y,dv.x);
  float ph=phase*.15+t*edgeMove;
  float wave=sin(ang*7.+ph)*.6+sin(ang*13.-ph*1.7)*.4;
  float env=1.+edgeAmp*wave;
  float rr=r*env*(.95+.1*n(uv*6.+t*.4));
  m+=amt*smoothstep(rr,rr*.15,d);
  gl_FragColor=vec4(min(m,1.2),0.,0.,1.);
}`;

const COMP = `#extension GL_OES_standard_derivatives : enable
precision highp float;varying vec2 uv;
uniform sampler2D real;uniform sampler2D robot;uniform sampler2D mask;
uniform float t;uniform float full;uniform int mode;uniform float mixv;
uniform vec4 rect;uniform vec2 par;uniform float aspect;uniform float waves;uniform float cap;
uniform float envBlend;uniform float envReact;uniform float showEnv;uniform vec2 ptr;uniform sampler2D txt;uniform sampler2D smask;uniform float glassOn;uniform float refr;uniform float split;uniform float scanD;uniform float placeholder;uniform float xray;uniform float xrayRate;uniform float trailW;uniform float bgPulse;uniform float dSpec;uniform float dRib;uniform float dVinyl;uniform float dBloom;uniform float dDust;uniform vec2 lp;uniform float thumpV;uniform float energy;uniform float breathV;uniform float shadowOn;uniform float grainOn;uniform float vigOn;uniform float lightOn;uniform float softOn;uniform float chestOn;uniform vec2 eyeOff;uniform float eyeGlowV;uniform float focusP;uniform float blinkV;uniform float sweepOn;uniform float reflOn;uniform float earL;uniform float earR;uniform float mouthV;uniform float earOn;uniform float mouthOn;uniform float inkOn;uniform float lampOn;uniform float breatheOn;uniform float ptrOn;uniform vec3 pts[16];uniform float pencilOn;uniform float dustOn;uniform float irisP;uniform vec2 irisC;uniform float greenOn;uniform vec3 accentCol;${NOISE}

uniform float rlOn;uniform float rlSpeed;uniform float rlPulse;uniform float rlBreath;uniform float rlWake;uniform float rlRange;uniform float rlGlow;uniform float rlCur;uniform float rlNeon;uniform float rlBloom;uniform float rlCon;
uniform float cuOn;uniform float cuAmt;uniform float cuSpeed;uniform float cuHand;uniform vec3 spk[4];uniform float spAmt;uniform float spR;
uniform vec2 piv;uniform float gStreams;uniform float gSpeed;uniform float gCurve;uniform float seamX;uniform float seamY;uniform float liveR;uniform float seamJag;uniform float seamGlow;uniform float mob;
// 2.5D pivot: the portrait plane turns on an axis anchored at the lower chest
vec2 pivotW(vec2 q){vec2 p=vec2(.5,.85);vec2 d=q-p;
  float w=1.+d.x*piv.x-d.y*piv.y;
  d/=max(w,.2);d.x+=piv.x*.06*min(d.y,0.);return p+d;}
float silAt(vec2 u){vec2 q=(u-rect.xy)/rect.zw;q.y=1.-q.y;q=pivotW(q);
  if(q.x<0.||q.x>1.||q.y<0.||q.y>1.)return 0.;return texture2D(smask,q).r;}
float silBlur(vec2 u,float r){float s=silAt(u)*.2;
  for(int i=0;i<8;i++){float a=float(i)*.785;s+=silAt(u+vec2(cos(a)/aspect,sin(a))*r)*.1;}return s;}
// resonant strings: calm, breathing, bent slowly by cursor energy, coupled to neighbours, flowing around Damian
float strings(vec2 u){
  vec2 p=u*vec2(aspect,1.);float L=0.;
  float breath=.006*sin(t*.78);
  float body=silBlur(u,.06);
  for(int i=0;i<9;i++){
    float fi=float(i);
    float y0=.1+fi*.09+breath;
    float tr=.05*sin(t*.35+fi);                           // energy drifts slowly along the string
    float e=0.;
    for(int k=-2;k<=2;k++){float dx=float(k)*.03;float wk=1.-abs(float(k))*.25;
      e+=wk*max(texture2D(mask,vec2(u.x+dx,y0)).r,texture2D(mask,vec2(u.x+dx+tr,y0)).r*.8);
      e+=wk*.35*(texture2D(mask,vec2(u.x+dx,y0+.09)).r+texture2D(mask,vec2(u.x+dx,y0-.09)).r);}
    e/=3.5;
    e=smoothstep(0.,1.,min(e,1.4)/1.4)*1.4;
    float sp=.12+.06*fract(fi*.618);
    float rest=.008*sin(p.x*1.2+t*sp+fi*2.)+.003*sin(p.x*2.3-t*sp*.7+fi);
    float pl=smoothstep(0.,1.4,e)*1.2*trailW*.026*sin(p.x*3.5-t*.45+fi*1.3);
    float y=y0+rest+pl+body*.035;                          // lift gently around the silhouette
    float a=(.5+.6*min(e,1.))*(1.+bgPulse*.2*sin(t*.4+fi));
    float dy=u.y-y;float d=dy/max(fwidth(dy),1e-4);
    L+=a*exp(-d*d*.35);
  }
  return L;
}
vec3 human(vec2 u){
  vec2 p=u*vec2(aspect,1.);
  vec3 c=mix(vec3(.885,.875,.86),vec3(.965,.958,.945),smoothstep(0.,1.,u.y));
  float v=length((u-vec2(.5,.62))*vec2(aspect*.8,1.));
  c+=vec3(.05,.045,.035)*(1.-smoothstep(0.,.75,v));      // soft radial key light behind head
  c*=1.-.2*smoothstep(.35,1.25,length((u-vec2(.5,.55))*vec2(aspect,1.))); // environmental falloff
  float lines=sin((u.y+.015*sin(p.x*3.+t*.15))*220.);
  c-=.006*smoothstep(.7,1.,lines)*smoothstep(.0,.6,u.y);  // barely-there frequency surface
  c-=.07*exp(-pow((u.x-.5)*aspect*1.8,2.))*smoothstep(.4,0.,u.y); // floor shadow under Damian
  c+=vec3(.012,.012,.014)*smoothstep(.25,0.,u.y);         // low haze
  // faint technical grid, fading with distance from centre
  vec2 gq=abs(fract((p+vec2(t*.003,0.))*12.)-.5);
  float gl=1.-smoothstep(0.,.02,min(gq.x,gq.y));
  c-=.012*gl*smoothstep(1.1,.2,length((u-vec2(.5,.5))*vec2(aspect,1.)));
  // drifting contour lines (iso-lines of a slow, gently swelling noise field)
  vec2 sw=vec2(sin(t*.11),cos(t*.09))*.06;
  float f=fbm(p*1.4+sw+vec2(t*.03,-t*.02))*9.;
  float iso=abs(fract(f)-.5);float w=fwidth(f);
  c-=.035*(1.-smoothstep(0.,w*1.2,iso))*smoothstep(.05,.5,u.y);
  c-=vec3(.075)*strings(u);
  c+=(h(u*900.+t)-.5)*.012;                               // grain
  return c;
}
vec3 machine(vec2 u){
  vec2 p=u*vec2(aspect,1.);
  vec3 c=mix(vec3(.018,.019,.022),vec3(.06,.063,.07),u.y);
  c+=vec3(.07,.072,.08)*(1.-smoothstep(0.,.75,length((u-vec2(.5,.6))*vec2(aspect*.8,1.))));
  vec2 g=abs(fract(p*18.)-.5);float grid=1.-smoothstep(0.,.025,min(g.x,g.y));
  vec2 g2=abs(fract(p*90.)-.5);float fine=1.-smoothstep(0.,.04,min(g2.x,g2.y));
  c+=grid*.03+fine*.006;
  for(int i=0;i<5;i++){
    float fi=float(i);
    float amp=.025*(1.+fi*.3)*sin(p.x*.8+fi*1.7+t*.1);
    float y=.22+fi*.13+amp*sin(p.x*(5.+fi*2.)+t*(.4+fi*.07));
    c+=vec3(.55,.58,.62)*.09*exp(-abs(u.y-y)*700.);
  }
  float rings=sin(length((u-vec2(.5,.66))*vec2(aspect,1.))*90.-t*.8);
  c+=.012*smoothstep(.85,1.,rings)*(1.-smoothstep(.1,.7,length((u-vec2(.5,.66))*vec2(aspect,1.))));
  float sy=.12;float head=fract(t*.07);
  c+=vec3(1.,.42,.1)*exp(-abs(u.y-sy)*2200.)*(.08+.9*exp(-pow((u.x-head)*7.,2.)));
  c+=(h(u*900.+t)-.5)*.02;
  return c;
}

// PLACEHOLDER robot: chrome treatment of the real portrait until final robot art arrives
vec3 chrome(vec3 c,vec2 p){
  float l=smoothstep(.04,.85,dot(c,vec3(.3,.59,.11)));
  float sheen=.5+.5*sin(l*14.+p.y*9.-p.x*4.+t*.3);
  vec3 o=mix(vec3(.05,.06,.075),vec3(.86,.9,.97),pow(l,1.3));
  return o+vec3(.28,.3,.34)*pow(sheen,8.);
}
void main(){
  float raw=texture2D(mask,uv).r;
  float wob=(fbm(uv*3.+t*.12)-.5);
  float e=raw+wob*.16;
  // smooth liquid core
  float liquid=smoothstep(.34,.56,e);
  // soft ripples outside the pool edge: refraction only, never slices the image
  float band=smoothstep(.03,.1,e)*(1.-smoothstep(.2,.27,e));
  float ripple=sin(e*60.-t*5.)*band*waves;
  float m=min(liquid,cap);
  float fullM=smoothstep(0.,1.,full*1.35-uv.y*.35);
  m=max(m,fullM);
  // the split: jagged, stepped digital seam (machine to the right)
  float sy=floor(uv.y*26.);
  float sj=mix((fract(sin(sy*91.7+floor(t*1.6)*3.1)*43758.5)-.5),(sin(sy*91.7+t*.9)*.5+sin(sy*37.3-t*.6)*.5)*.5,mob)*seamJag*.012+sin(uv.y*7.+t*.6)*seamJag*.004;
  float sx=seamX+sj+sin(t*.5)*.003+thumpV*.002;
  float stip=seamY+(fract(sin(floor(uv.x*aspect*40.)*17.3+floor(t*2.))*4375.5)-.5)*.02;
  float sd=step(sx,uv.x)*step(uv.y,stip);
  // locked split: the human side keeps its own world; no underworld or robot from the cursor there
  float lockV=step(-.02,seamX)*step(seamX,1.02)*smoothstep(.55,.95,seamY);
  float hs=lockV*(1.-smoothstep(sx-.02,sx+.02,uv.x));
  m=mix(m,fullM,hs);
  m=max(m,sd);
  if(mode==1)m=mixv;
  if(mode==2)m=step(mixv,uv.x);

  // environment
  vec2 buv=uv+par*.012;
  float env=clamp(envBlend,0.,1.); // light world by default
  float hc=0.; // headline no longer masked out; it reacts to the dark world instead
  float bass=pow(sin(t*1.57)*.5+.5,6.); // slow ~4s bass swell
  // bold neon world: near-black, strings glow electric blue + copper
  float dark=max(smoothstep(.08,.5,e)*clamp(envReact,0.,1.)*(1.-hs),sd);
  // light world: soft contact shadow grounding Damian
  vec2 cs=(uv-vec2(rect.x+rect.z*.5,0.))*vec2(aspect*1.2,4.);
  vec3 bg=mix(human(buv),machine(buv),env*showEnv);
  bg*=1.-.14*exp(-dot(cs,cs))*(1.-env)*showEnv;
  if(dark>.001){
    vec2 suv=uv+par*.02; // strings sit on a nearer depth plane than the far grid
    float sl=strings(suv);
    // slow travelling pulses of light along the strings
    float tp=pow(sin(suv.x*aspect*2.4-t*.45+floor(suv.y*9.)*1.7)*.5+.5,10.);
    float kph=fract(t*.22);float kick=smoothstep(0.,.18,kph)*exp(-kph*3.5)*dBloom; // slow 'kick' every ~4.5s
    vec3 deep=vec3(.004,.006,.012)+machine(buv)*.28+mix(vec3(.008,.014,.05),vec3(.03,.01,.05),uv.x)*(.5+.6*uv.y);
    vec2 hcn0=rect.xy+rect.zw*vec2(.49,.72);
    // vinyl grooves: far plane, slowly rotating sheen
    if(dVinyl>0.){vec2 vq=(uv-hcn0+par*.004)*vec2(aspect,1.);float vd=length(vq);float ang=atan(vq.y,vq.x);
      float gr=1.-smoothstep(.0,.12,abs(fract(vd*20.)-.5));float sh=pow(.5+.5*cos(2.*ang-t*.12),6.);
      deep+=mix(vec3(.15,.4,.9),vec3(.9,.5,.25),sh)*gr*(.12+sh*.6)*smoothstep(.14,.22,vd)*smoothstep(.8,.45,vd)*dVinyl*.14;}
    // neon dust drifting on three depth planes
    if(dDust>0.){float dust=0.;for(int l=0;l<3;l++){float fl=float(l);
      vec2 du=(uv+par*(.004+fl*.014))*vec2(aspect,1.)*(10.+fl*8.)+vec2(t*.01,-t*(.02+fl*.014)); // drifts upward
      vec2 id=floor(du);vec2 f=fract(du)-.5;float h=fract(sin(dot(id,vec2(127.1,311.7)))*43758.5);
      vec2 o=vec2(fract(h*13.)-.5,fract(h*7.)-.5)*.6;
      dust+=step(.82,h)*smoothstep(.07-fl*.015,0.,length(f-o))*(.45+.4*sin(t*.4+h*30.))*(.5+fl*.3);}
      deep+=mix(vec3(.4,.7,1.),vec3(1.,.65,.4),step(.93,fract(dust*7.)))*dust*dDust*.5;}
    // waveform ribbons drifting slowly at different depths
    if(dRib>0.){float rib=0.;for(int i=0;i<3;i++){float fi=float(i);vec2 ru=uv+par*(.01+fi*.01);
      float y=.5+fi*.13+.06*sin(ru.x*aspect*(1.6+fi*.7)+t*(.08+fi*.03))*sin(t*.05+fi*2.)+.015*sin(ru.x*aspect*8.-t*(.2+fi*.05));
      float d=ru.y-y;rib+=(exp(-d*d*12000.)*.8+exp(-d*d*400.)*.06)*(.55+.45*sin(ru.x*3.+t*.15+fi*2.));}
      deep+=mix(vec3(.2,.5,1.),vec3(.65,.35,1.),.45)*rib*dRib*.5*(1.+kick*.5);}
    // spectrum horizon: soft bars breathing above the floor, with a faint reflection
    if(dSpec>0.&&false){vec2 su2=uv+par*vec2(.008,0.);float bx=floor(su2.x*72.);float by=su2.y-.32;
      float bh=(.012+.11*pow(fbm(vec2(bx*.21,t*.06)),2.5)*(1.-.6*abs(su2.x-.5)*2.))*(1.+kick*.8);
      float col1=step(abs(fract(su2.x*72.)-.5),.3);
      float bar=col1*step(0.,by)*(1.-smoothstep(bh-.004,bh,by))*(.25+.75*by/max(bh,.001));
      float refl=col1*step(by,0.)*(1.-smoothstep(0.,bh*.6,-by))*.18;
      deep+=mix(vec3(.15,.5,1.),vec3(1.,.5,.22),smoothstep(.2,.8,su2.x))*(bar+refl)*dSpec*.55;}
    deep+=(vec3(.15,.55,1.)*sl*(1.1+tp*1.8)+vec3(1.,.5,.2)*sl*(.18+tp*.5))*(1.+bass*.35);
    // growth signals: rising streams on three depth planes
    if(gStreams>0.){float st=0.;for(int l=0;l<3;l++){float fl=float(l);
      vec2 su3=(uv+par*(.006+fl*.012));float cols=26.+fl*18.;float cx=su3.x*aspect*cols;float id=floor(cx);
      float h=fract(sin(id*78.233+fl*11.)*43758.5);if(h<.72)continue;
      float sp=(.035+h*.05)*(1.+fl*.4)*gSpeed*(1.+thumpV*.25);
      float y=fract(su3.y*(.6+fl*.2)-t*sp+h*9.);
      float len=.12+h*.1;float tail=smoothstep(len,0.,y)*smoothstep(0.,.01,y);
      float w=exp(-pow((fract(cx)-.5)*(3.-fl*.8),2.)*90.);
      st+=tail*tail*w*(.25+fl*.3);}
      deep+=vec3(.25,.55,1.)*st*gStreams*.35;}
    // growth curve: exponential line that draws itself in, then fades and redraws
    if(gCurve>0.){float cyc=fract(t/12.);float gx=(uv.x-.08)/.84;
      float gy=.12+.62*(exp(gx*3.)-1.)/(exp(3.)-1.);
      float gd=abs(uv.y-gy)*aspect;float draw=step(gx,cyc*1.4)*step(0.,gx)*step(gx,1.);
      float fade=smoothstep(0.,.1,cyc)*(1.-smoothstep(.8,1.,cyc));
      float head=exp(-pow((gx-cyc*1.4)*10.,2.));
      deep+=vec3(.35,.7,1.)*(exp(-gd*gd*90000.)*.35+exp(-gd*gd*2500.)*.05+head*exp(-gd*gd*20000.)*.6)*draw*fade*gCurve;}
    // far grid (deepest plane)
    vec2 fu=uv+par*.006;
    vec2 gq=abs(fract(fu*vec2(aspect,1.)*18.)-.5);
    deep+=vec3(.06,.18,.35)*smoothstep(.485,.5,max(gq.x,gq.y))*.1;
    // perspective floor grid receding to a horizon behind him
    float hz=.32-uv.y;
    if(hz>0.&&false){
      float z=.06/hz;
      vec2 fg=vec2((uv.x-.5+par.x*.01)*aspect*z,z+t*.05);
      vec2 fq=abs(fract(fg*2.)-.5);
      float fl=smoothstep(.47,.5,max(fq.x,fq.y))*smoothstep(0.,.12,hz);
      deep+=vec3(.12,.4,.85)*fl*.22*(1.+bass*.5);
    }
    // circular sound waves slowly emitted from the cursor
    float cd=length((uv-ptr)*vec2(aspect,1.));
    float rings=0.;
    for(int i=0;i<3;i++){
      float r=fract(t*.22+float(i)/3.)*.5;
      rings+=exp(-pow((cd-r)*160.,2.))*(1.-r/.5)*(1.-r/.5);
    }
    deep+=mix(vec3(.25,.65,1.),vec3(1.,.55,.25),.3)*rings*.55*(.8+bass*.3);
    // speaker pulses breathing out from his head
    vec2 hcn=rect.xy+rect.zw*vec2(.49,.72);
    float hd=length((uv-hcn)*vec2(aspect,1.));
    float sp2=0.;
    for(int i=0;i<2;i++){
      float r=.08+fract(t*.12+float(i)*.5)*.35;
      sp2+=exp(-pow((hd-r)*220.,2.))*(1.-smoothstep(.08,.43,r));
    }
    deep+=vec3(.2,.55,1.)*sp2*.3;
    // soft acoustic aura around the cursor
    deep+=vec3(.05,.22,.5)*exp(-pow(cd*3.2,2.))*.4;
    deep*=1.+kick*.3;deep+=vec3(.03,.07,.18)*kick*.35;
    bg=mix(bg,deep,clamp(dark*1.3,0.,1.)*showEnv);
  }

  // contact shadow + faint warm floor light where he meets the frame
  float fcx=rect.x+rect.z*.51;
  float pool=exp(-pow((uv.x-fcx)*aspect/(rect.z*aspect*.55),2.))*smoothstep(.22,0.,uv.y);
  bg*=1.-shadowOn*.14*pool*(1.-dark*.6);
  bg+=vec3(.05,.03,.012)*shadowOn*pool*smoothstep(.0,.12,uv.y)*(1.-dark);
  // sub-bass ripple rising from the chest (optional)
  if(chestOn>0.){vec2 cc=vec2(fcx,rect.y+rect.w*.62);float ph=fract(t/9.);float dd=length((uv-cc)*vec2(aspect,1.));
    bg+=mix(vec3(.02,.025,.03),vec3(.15,.4,.8),dark)*chestOn*exp(-pow((dd-ph*.9)/.035,2.))*(1.-ph)*.6;}
  // portrait (registered textures, same uv)
  vec2 puv=(uv-rect.xy-par*.03)/rect.zw;puv.y=1.-puv.y;
  puv=(puv-vec2(.5,1.))/(1.+breathV)+vec2(.5,1.); // breathing, anchored at the base
  puv=pivotW(puv);
  float hw=pow(clamp(1.-puv.y,0.,1.),1.5);
  float inside=step(0.,puv.x)*step(puv.x,1.)*step(0.,puv.y)*step(puv.y,1.);
  float edgeL=liquid*(1.-liquid)*4.;
  vec2 grad=vec2(dFdx(raw),dFdy(raw));
  vec2 off=grad*(ripple*1.5-edgeL*1.2);
  float edge=edgeL;
  vec4 A=texture2D(real,puv)*inside;
  // living eyes: shift the lens inside each socket toward the cursor
  vec2 eL=vec2(.4467,.2787),eR=vec2(.5446,.2787),er=vec2(.014,.009);
  float wL=smoothstep(1.,.3,length((puv-eL)/er)),wR=smoothstep(1.,.3,length((puv-eR)/er));
  float ew=max(wL,wR);vec2 ec=wL>wR?eL:eR;
  vec4 B=texture2D(robot,puv-eyeOff*.35*ew)*inside; // pupils shift slightly; sockets stay put
  if(placeholder>.5)B=vec4(chrome(A.rgb,puv),A.a);
  {
    vec2 eo=puv-(length(puv-eL)<length(puv-eR)?eL:eR);
    float near=step(length(eo/vec2(.03,.02)),1.);
    // glowing lens dot carries the look
    vec2 ip=(eo-eyeOff)/vec2(.006,.0045);float id=length(ip);
    float iris=exp(-id*id*mix(2.2,4.,focusP));
    vec2 hp=(eo-eyeOff*1.15-vec2(-.0015,-.0012))/vec2(.0016,.0013);
    float hl=exp(-dot(hp,hp)*2.);
    float ring=exp(-pow((id-1.4-focusP*.9)/.25,2.))*focusP;
    B.rgb+=vec3(.45,.8,1.)*(iris*eyeGlowV*.9+ring*.7)*near+vec3(1.)*hl*.5*near;
    float lid=smoothstep(.2+blinkV*.9,.0+blinkV*.9,abs(eo.y)/.014*(1.-blinkV*.85));
    B.rgb*=mix(1.,.15,blinkV*(1.-lid)*near);
  }
  // headphones: slow ring of blue light around each ear cup
  for(int k=0;k<2;k++){
    vec2 ecu=k==0?vec2(.357,.302):vec2(.628,.297);float hv=k==0?earL:earR;
    vec2 eq=(puv-ecu)/vec2(.031,.075);float r0=length(eq);
    float ang=atan(eq.y,eq.x);
    float rg=exp(-pow((r0-.82)/.07,2.))*(.55+.45*sin(ang*2.-t*1.4));
    float inner=exp(-r0*r0*3.)*(.5+.5*sin(t*1.8));
    B.rgb+=vec3(.35,.72,1.)*(rg*.9+inner*.15)*hv*earOn;
  }
  // mouth: a thin voice-meter of light along the lip seam
  {
    vec2 mq=(puv-vec2(.495,.411))/vec2(.044,.0045);
    float seg=step(.35,fract(mq.x*4.));
    float lvl=.5+.5*sin(floor(mq.x*4.)*1.7+t*6.);
    float mbar=exp(-mq.y*mq.y)*smoothstep(1.,.6,abs(mq.x))*seg*(.4+.6*lvl);
    B.rgb+=vec3(.4,.78,1.)*mbar*mouthV*mouthOn*.8;
  }
  // breath-synced chrome sweep across face, shoulders and arms
  float lumB=dot(B.rgb,vec3(.33));
  float sw=fract(t/7.5);float swBand=exp(-pow(((puv.x*.6+puv.y)-(sw*2.2-.4))/.06,2.));
  B.rgb+=vec3(.85,.93,1.)*swBand*sweepOn*.28*smoothstep(.35,.9,lumB)*(.7+.3*sin(t*1.0472));
  // cursor reflection: soft blurred highlight on the metal
  float rd=length((uv-lp)*vec2(aspect,1.));
  B.rgb+=vec3(.8,.88,1.)*exp(-rd*rd*30.)*reflOn*.22*smoothstep(.3,.85,lumB);
  // living blue lines: several currents of shifting blues travel through the existing circuitry (lines never move)
  if(rlOn>0.&&inside>0.){
    float bm=smoothstep(.05,.25,B.b-max(B.r,B.g*.85))*smoothstep(.15,.5,B.b);
    // soft halo: blurred copy of the blue mask so the light spills onto the chrome
    float halo=0.;
    if(rlGlow>0.){
      vec2 go=vec2(.006,.0075)*rlGlow;
      for(int k=0;k<4;k++){float an=float(k)*1.5708+.3;vec4 q=texture2D(robot,puv+vec2(cos(an),sin(an))*go);
        halo+=smoothstep(.05,.25,q.b-max(q.r,q.g*.85))*smoothstep(.15,.5,q.b);}
      halo=halo/4.*inside;
    }
    float ts=t*rlSpeed;
    // overlapping currents at different angles/speeds
    float cur=0.;
    for(int k=0;k<3;k++){
      float fk=float(k);
      if(fk>=rlCur) break;
      vec2 dir=vec2(cos(fk*2.1+.4),sin(fk*2.1+.4));
      float ph=fract(ts/(3.6+fk*1.3)+fk*.37);
      float d=dot(puv-.5,dir)-(ph*1.8-.9);
      cur+=exp(-d*d/(.006+fk*.002))*(1.-fk*.2);
    }
    float fl=fbm(puv*6.+vec2(ts*.08,-ts*.11));
    float br=rlBreath*.35*(.5+.5*sin(t*1.0472));
    float wk=exp(-rd*rd*22.)*rlWake*(.6+.4*sin(rd*40.-t*2.5))*ptrOn;
    // 4-stop blue ramp: navy -> electric -> cyan -> icy white
    float e=clamp(fl*.55+cur*rlPulse*.8+wk*.6,0.,1.);
    vec3 c0=vec3(.04,.1,.35),c1=vec3(.1,.38,1.),c2=vec3(.2,.85,1.),c3=vec3(.85,.96,1.);
    vec3 ramp=e<.33?mix(c0,c1,e/.33):e<.66?mix(c1,c2,(e-.33)/.33):mix(c2,c3,(e-.66)/.34);
    vec3 tint=mix(vec3(.25,.55,1.),ramp,rlRange);
    // wide bloom: second, larger ring of samples
    float bloom=0.;
    if(rlBloom>0.){
      vec2 bo=vec2(.016,.02)*rlBloom;
      for(int k=0;k<4;k++){float an=float(k)*1.5708+1.08;vec4 q=texture2D(robot,puv+vec2(cos(an),sin(an))*bo);
        bloom+=smoothstep(.05,.25,q.b-max(q.r,q.g*.85))*smoothstep(.15,.5,q.b);}
      bloom=bloom/4.*inside;
    }
    float flick=.94+.06*sin(t*31.)*sin(t*7.3+puv.y*9.);
    float lit=(.55+br+cur*rlPulse*2.2+wk*1.6)*flick*rlNeon;
    // darken nearby chrome so the tubes pop
    float near=max(max(halo,bloom)-bm,0.);
    B.rgb*=1.-near*rlCon*.45;
    // neon tube: saturated edge, near-white hot core
    vec3 edgeC=tint*vec3(.55,.85,1.15);
    vec3 core=mix(edgeC,vec3(.9,.97,1.),smoothstep(.55,1.,bm)*.75);
    B.rgb+=core*bm*lit*.9;
    B.rgb+=edgeC*near*lit*.55;
    B.rgb+=edgeC*bloom*lit*.35;
  }
  // copper heartbeat: amber parts glow slowly like warm filaments; brighten as a blue current passes
  if(cuOn>0.&&inside>0.){
    float cm=smoothstep(.12,.32,B.r-B.b)*smoothstep(.25,.6,B.r);
    if(cm>0.){
      float hb=pow(.5+.5*sin(t*cuSpeed*1.75),3.);
      vec2 dir=vec2(cos(.4),sin(.4));
      float d=dot(puv-.5,dir)-(fract(t*rlSpeed/3.6)*1.8-.9);
      float hand=exp(-d*d/.012)*cuHand;
      float wm=exp(-rd*rd*26.)*ptrOn*.5;
      float g=(hb*.6+hand+wm+thumpV*.4)*cuAmt;
      B.rgb+=vec3(1.,.48,.14)*cm*g*.55+vec3(1.,.8,.5)*cm*cm*g*.15;
    }
  }
  // body speakers: one soft bass thump per hover, slow rings ripple out
  for(int k=0;k<4;k++){
    vec3 sp=spk[k];float age=sp.z;
    if(age<0.||age>1.6)continue;
    vec2 q=(puv-sp.xy)/vec2(.8,1.)/spR;float r0=length(q);
    float env=exp(-age*2.2);
    float cone=exp(-r0*r0/.0012)*exp(-age*6.)*1.2;
    float rings=0.;
    for(int j=0;j<3;j++){float rr=.02+(age-float(j)*.18)*.06;
      if(rr>.02)rings+=exp(-pow((r0-rr)/.005,2.))*(1.-float(j)*.25);}
    vec3 rc=mix(vec3(.35,.72,1.),vec3(1.,.55,.22),.25);
    B.rgb+=(rc*rings*.7+vec3(.6,.85,1.)*cone)*env*spAmt*inside;
  }
  float sil=texture2D(smask,puv).r*inside;
  B.rgb=mix(A.rgb,B.rgb,smoothstep(.6,.98,sil)); // hide the robot's white backdrop at the outline
  B.a=A.a;
  m=max(min(m,smoothstep(.35,.9,sil)),fullM*sil);
  if(softOn>0.){vec2 bo=vec2(.0012,.0015)*softOn;vec4 Ab=(texture2D(real,puv+bo)+texture2D(real,puv-bo)+texture2D(real,puv+vec2(bo.x,-bo.y))+texture2D(real,puv-vec2(bo.x,-bo.y)))*.25*inside;
    float ez=edgeL*smoothstep(.3,.8,sil);A=mix(A,Ab,ez*.8);}
  // cursor light: chrome catches a soft moving key light
  float spec=pow(max(0.,1.-length((uv-lp)*vec2(aspect,1.))*1.4),3.);
  B.rgb+=B.rgb*vec3(.85,.92,1.)*spec*lightOn*.45*smoothstep(.35,.85,dot(B.rgb,vec3(.33)));
  vec4 P=mix(A,B,m);
  // living X-ray: white hairlines flowing along his seams and drifting off his silhouette
  float lineIn=0.,lineOut=0.;
  if(xray>0.&&mode==0){
    float breathe=.8+.2*sin(t*.6);
    float wake=smoothstep(.0,.3,raw);
    vec2 px=1./vec2(1920.,1536.)*1.3;
    float lc=dot(texture2D(robot,puv).rgb,vec3(.33));
    float gx=dot(texture2D(robot,puv+vec2(px.x,0.)).rgb,vec3(.33))-lc;
    float gy=dot(texture2D(robot,puv+vec2(0.,px.y)).rgb,vec3(.33))-lc;
    float seam=smoothstep(.12,.32,length(vec2(gx,gy)))*smoothstep(.6,.95,sil)*(1.-m);
    float flow=smoothstep(.55,1.,sin(fbm(puv*3.)*18.-t*xrayRate*9.)*.5+.5); // light travelling along fibres
    float face=1.-.75*exp(-pow((puv.x-.49)*5.,2.)-pow((puv.y-.3)*4.,2.));
    lineIn=seam*flow*face*(breathe+wake)*xray*.3;
    // field lines leaving the body: iso-lines of blurred silhouette, moving outward
    float sb=silBlur(uv,.05)*.6+silBlur(uv,.12)*.4;
    float fld=sb*12.+t*xrayRate*2.5+fbm(uv*vec2(aspect,1.)*3.+t*.03)*1.5;
    float iso=1.-smoothstep(0.,fwidth(fld)*1.8,abs(fract(fld)-.5));
    float shell=smoothstep(.02,.15,sb)*smoothstep(.75,.35,sb);
    lineOut=iso*shell*(breathe*.7+wake*.5)*(1.+thumpV*.8)*(.5+.5*energy)*xray*(1.-hc)*.2;
  }
  vec3 col=mix(bg,P.rgb,P.a);
  col=mix(col,vec3(1.),lineIn*P.a);
  col=mix(col,mix(vec3(1.),vec3(.35,.37,.4),.55*(1.-dark)),lineOut*(1.-P.a)); // white on dark, soft graphite on light
  // human-side wipe: a light paper sheet comes down from the top while the machine seam rises
  if(seamX<1.02&&seamX>-.02&&mode==0){
    float lw=1.-smoothstep(sx-.004,sx+.004,uv.x);
    float tl=1.-seamY+(fbm(vec2(uv.x*aspect*18.,t*.2))-.5)*.012; // soft hand-torn edge
    float above=smoothstep(tl-.003,tl+.003,uv.y)*lw;
    col=mix(col,mix(col,vec3(1.,.99,.975),.35),above*step(.001,seamY)*(1.-P.a)); // keep Damian crisp
    col=mix(col,vec3(.25,.24,.23),exp(-pow((uv.y-tl)/.0012,2.))*lw*.35*step(.01,seamY)*step(seamY,.99)*(1.-P.a*.8));
  }
  // human side (locked split): ink in water + paper and lamp light
  if(hs>.001&&mode==0){
    vec2 iq=uv*vec2(aspect,1.);
    float curl=fbm(iq*4.+vec2(t*.04,-t*.03)+fbm(iq*2.-t*.02)*1.5);
    float inkM=smoothstep(.06,.55,e+(curl-.5)*.35)*inkOn*hs;
    float fil=smoothstep(.35,.75,curl)*.6+.4;
    float glowM=smoothstep(.02,.6,raw)*inkOn*hs; // soft light glow, no rings or curls
    col=mix(col,vec3(1.,.995,.985),glowM*glowM*.28*(1.-P.a*.85)); // pure light ink: only ever brightens
    float ld=length((uv-lp)*vec2(aspect,1.));
    float lamp=exp(-ld*ld*9.)*lampOn*hs*ptrOn;
    float paper=fbm(iq*90.)*.6+fbm(iq*240.)*.4;
    col*=1.+lamp*vec3(.16,.1,.04);
    col+=(paper-.5)*.05*lamp;
    col*=1.-lamp*.12*smoothstep(.2,.0,dot(P.rgb,vec3(.33)))*P.a; // deeper shadows in skin & shirt folds
    // pencil trace: faint hand-drawn graphite following recent cursor points
    float pen=0.;
    for(int i=0;i<15;i++){
      vec3 p0=pts[i],p1=pts[i+1];
      if(p0.z>.001&&p1.z>.001){
        vec2 a2=p0.xy*vec2(aspect,1.),b2=p1.xy*vec2(aspect,1.),q=iq+vec2(fbm(iq*40.)-.5)*.004;
        vec2 ab=b2-a2;float h=clamp(dot(q-a2,ab)/max(dot(ab,ab),1e-6),0.,1.);
        float dd=length(q-a2-ab*h);
        float w=.0012*(.6+.8*fbm(iq*25.+float(i)));
        pen=max(pen,(1.-smoothstep(w*.4,w,dd))*min(p0.z,p1.z)*(.55+.45*fbm(iq*300.)));
      }
    }
    col=mix(col,vec3(.22,.21,.2),pen*.45*pencilOn*hs);
    // warm dust drifting in the lamp light
    vec2 dc=iq*55.+vec2(t*.15,-t*.25);vec2 di=floor(dc);
    float dh=fract(sin(dot(di,vec2(127.1,311.7)))*43758.5);
    vec2 dp=vec2(fract(dh*13.1),fract(dh*7.7))*.8+.1;
    float dsp=exp(-dot(fract(dc)-dp,fract(dc)-dp)*900.)*step(.86,dh)*(.6+.4*sin(t*2.+dh*30.));
    col+=vec3(1.,.85,.6)*dsp*lamp*dustOn*1.4;
  }
  // neon rim light on Damian's outline where the dark world touches him
  float nrim=smoothstep(.0,.25,length(vec2(dFdx(P.a),dFdy(P.a)))*6.);
  col+=mix(vec3(.2,.6,1.),vec3(1.,.55,.25),.25+.25*sin(t*.3))*nrim*dark*.55;
  col+=vec3(1.,.72,.45)*edgeL*softOn*.07*P.a*(1.-fullM)*(1.-hs); // warm seam where the worlds meet
  col+=vec3(1.)*(edgeL*.025+max(ripple,0.)*.012)*(1.-fullM)*P.a*(1.-hs); // glassy water highlight
  // rim light on silhouette in machine world
  float rim=clamp(length(vec2(dFdx(P.a),dFdy(P.a)))*3.,0.,1.);
  col+=vec3(.75,.8,.9)*rim*.25*env*showEnv;
  if(seamX<1.02&&seamX>-.02){
    float sdx=(uv.x-sx)*aspect;
    float core=exp(-sdx*sdx*60000.);float halo=exp(-sdx*sdx*1800.);
    float fl=.75+.25*sin(t*23.+uv.y*80.);
    float below=step(uv.y,seamY);
    col+=vec3(1.)*core*seamGlow*fl*below;
    float tipd=length(vec2(sdx,(uv.y-seamY)));col+=vec3(.75,.9,1.)*exp(-tipd*tipd*9000.)*1.2*step(seamY,.99)*step(.01,seamY);
    col+=mix(vec3(.3,.65,1.),vec3(1.,.55,.25),step(0.,sdx))*halo*.18*seamGlow*below;
    col.r+=exp(-pow(sdx+.004,2.)*30000.)*.35*seamGlow;col.b+=exp(-pow(sdx-.004,2.)*30000.)*.35*seamGlow;
    float spk=step(.985,fract(sin(floor(uv.y*140.)*12.9+floor(t*9.))*4375.5))*exp(-abs(sdx)*300.)*P.a;
    col+=vec3(.6,.85,1.)*spk*seamGlow;
  }

  // headline: clean ink, analogue interference where the wave passes
  vec2 tu=uv-par*.02;
  if(mode==0){
    float gm=smoothstep(.25,.7,e)*glassOn;gm=gm*gm*(3.-2.*gm);
    gm=max(gm,liveR*step(rect.x+rect.z*.5,uv.x)*(.5+.12*sin(t*1.3)+.1*step(.92,fract(t*.7))+focusP*.5*step(.5,fract(t*14.))));
    vec3 ink=mix(vec3(.07,.075,.08),vec3(.93,.92,.9),env);
    // in the neon world the letters become glowing signal light
    vec3 neonInk=mix(vec3(.35,.75,1.),vec3(1.,.62,.32),smoothstep(.3,.7,uv.x))*(1.1+bass*.4);
    ink=mix(ink,neonInk,smoothstep(.05,.6,dark));
    ink=mix(ink,vec3(.95,.95,.97),sd);
    float ty=1.-tu.y;
    // HUMAN breathes: a slow plucked-string vibration with a warm glow on the left words
    float leftT=1.-step(rect.x+rect.z*.5,uv.x);
    float hb=leftT*breatheOn*(exp(-dot((uv-lp)*vec2(aspect,1.),(uv-lp)*vec2(aspect,1.))*14.)*(ptrOn)+hs*.25);
    gm*=1.-leftT*hs; // no digital static on the human side once locked
    float sl=floor(ty*90.);
    float jit=sin(ty*40.+t*3.)*.002*gm+sin(tu.x*38.-t*2.2)*sin(t*1.1)*.0035*hb;
    vec2 tt=vec2(tu.x,ty+jit*(1.-gm)+0.)+vec2(sin(ty*40.+t*3.)*.002*gm,0.);
    ink=mix(ink,vec3(.55,.32,.18),min(hb,1.)*.45);
    float sp=split*.005*gm;
    vec3 rgb=vec3(texture2D(txt,tt+vec2(sp,0.)).a,texture2D(txt,tt).a,texture2D(txt,tt-vec2(sp,0.)).a);
    float ta=rgb.g;
    col+=vec3(1.,.6,.3)*texture2D(txt,tt+vec2(0.,.004)).a*min(hb,1.)*.06*(1.-ta);
    if(max(rgb.r,max(rgb.g,rgb.b))>.001){
      float scan=1.-.08*refr*(.5+.5*sin(gl_FragCoord.y*scanD));
      vec3 noisy=ink*scan;
      vec3 glitch=col*(1.-rgb)+rgb*noisy;
      col=mix(mix(col,ink,ta),glitch,gm);
    }
  }

  if(mode==3){col=mix(bg*.4,vec3(A.a,sil,B.a),max(max(A.a,B.a),sil)*.85);} // R=photo G=mask B=robot
  if(mode==4){col=vec3(raw*.8,m,m);col=mix(col,vec3(1.,.4,.1),edge);}
  if(mode==0){
    col*=1.-thumpV*.018; // the room leans in on the beat
    col*=1.-vigOn*.2*smoothstep(.5,1.15,length((uv-.5)*vec2(aspect*.75,1.)));
    col+=(fract(sin(dot(floor(gl_FragCoord.xy)+floor(t*12.)*17.*(1.-mob),vec2(12.9898,78.233)))*43758.5453)-.5)*mix(.03,.02,mob)*grainOn;
  }
  // accent test: remap saturated blues (robot lines, glows, neon) to a calm Spotify green
  if(greenOn>0.){
    float bl=smoothstep(.04,.22,col.b-max(col.r,col.g*.9));
    col=mix(col,accentCol*(col.b/accentCol.g)+vec3(max(col.r-.3,0.))*.3,bl*greenOn);
  }
  // iris exit: the eye glow tightens to a point, then a circle of the page opens from it
  if(irisP>0.&&mode==0){
    float id2=length((uv-irisC)*vec2(aspect,1.));
    float pt=smoothstep(0.,.25,irisP)*(1.-smoothstep(.35,.6,irisP));
    col+=vec3(.6,.85,1.)*exp(-id2*id2*mix(400.,5000.,pt))*pt*1.6;
    float rad=pow(smoothstep(.25,1.,irisP),1.6)*1.6;
    float inC=1.-smoothstep(rad-.004,rad+.004,id2);
    col=mix(col,vec3(.945,.937,.918),inC);
    col+=vec3(.55,.8,1.)*exp(-pow((id2-rad)/.006,2.))*step(.26,irisP)*.6;
  }
  gl_FragColor=vec4(col,1.);
}`;

function compile(gl: WebGLRenderingContext, type: number, src: string) {
  const s = gl.createShader(type)!;
  gl.shaderSource(s, src);
  gl.compileShader(s);
  if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s) || "shader");
  return s;
}
function program(gl: WebGLRenderingContext, frag: string) {
  const p = gl.createProgram()!;
  gl.attachShader(p, compile(gl, gl.VERTEX_SHADER, VERT));
  gl.attachShader(p, compile(gl, gl.FRAGMENT_SHADER, frag));
  gl.bindAttribLocation(p, 0, "p");
  gl.linkProgram(p);
  return p;
}
function loadImage(src: string) {
  return new Promise<HTMLImageElement>((res, rej) => {
    const i = new Image();
    i.crossOrigin = "anonymous";
    i.onload = () => (i.decode ? i.decode().then(() => res(i), () => res(i)) : res(i));
    i.onerror = rej;
    i.src = src;
  });
}

const FluidReveal = ({ realSrc, robotSrc, maskSrc, robotPlaceholder, rect, settings, fx, onReady, lowPower, headline, headFont, overlayRoot, redrawKey, underline, onFail }: Props) => {
  const failRef = useRef(onFail); failRef.current = onFail;
  const ulRef = useRef(underline); ulRef.current = underline;
  const hlRef = useRef(headline); hlRef.current = headline;
  const hfRef = useRef(headFont); hfRef.current = headFont;
  const redrawRef = useRef<() => void>(() => {});
  useEffect(() => { const id = requestAnimationFrame(() => redrawRef.current()); return () => cancelAnimationFrame(id); }, [redrawKey, headFont, underline]);
  const phRef = useRef(!!robotPlaceholder); phRef.current = !!robotPlaceholder;
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [glGen, setGlGen] = useState(0); // bumps after a lost WebGL context is restored, rebuilding the scene
  const sRef = useRef(settings);
  sRef.current = settings;
  const rectRef = useRef(rect);
  rectRef.current = rect;

  useEffect(() => {
    const canvas = canvasRef.current!;
    const gl = canvas.getContext("webgl", { alpha: false, antialias: false, powerPreference: "high-performance" });
    if (!gl) { failRef.current?.(); return; }
    const onLost = (e: Event) => { e.preventDefault(); };
    const onRestored = () => setGlGen((g) => g + 1);
    canvas.addEventListener("webglcontextlost", onLost);
    canvas.addEventListener("webglcontextrestored", onRestored);
    gl.getExtension("OES_standard_derivatives");
    const hf = gl.getExtension("OES_texture_half_float");
    const hfl = gl.getExtension("OES_texture_half_float_linear");
    // iOS can expose half-float without linear filtering / renderability; fall back to bytes then
    let texType: number = hf && hfl ? hf.HALF_FLOAT_OES : gl.UNSIGNED_BYTE;

    let sim: WebGLProgram, comp: WebGLProgram;
    try { sim = program(gl, SIM); comp = program(gl, COMP); } catch (e) { console.error(e); failRef.current?.(); return; }

    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);

    let SW = 1, SH = 1;
    const targets: { tex: WebGLTexture; fb: WebGLFramebuffer }[] = [];
    const maxTex = gl.getParameter(gl.MAX_TEXTURE_SIZE) as number;
    const makeTargets = () => {
      const cw = canvas.clientWidth, ch = canvas.clientHeight;
      if (!(cw >= 1 && ch >= 1)) return false; // zero/invalid size (e.g. mid-resize): keep the last valid targets
      targets.forEach((t) => { gl.deleteTexture(t.tex); gl.deleteFramebuffer(t.fb); });
      targets.length = 0;
      const base = lowPower ? 256 : 512;
      const asp = cw / ch;
      SW = Math.max(1, Math.min(maxTex, Math.round(base * Math.max(1, asp)))); SH = Math.max(1, Math.min(maxTex, Math.round(base / Math.min(1, asp))));
      for (let i = 0; i < 2; i++) {
        const tex = gl.createTexture()!;
        gl.bindTexture(gl.TEXTURE_2D, tex);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, SW, SH, 0, gl.RGBA, texType, null);
        [gl.TEXTURE_MIN_FILTER, gl.TEXTURE_MAG_FILTER].forEach((p) => gl.texParameteri(gl.TEXTURE_2D, p, gl.LINEAR));
        [gl.TEXTURE_WRAP_S, gl.TEXTURE_WRAP_T].forEach((p) => gl.texParameteri(gl.TEXTURE_2D, p, gl.CLAMP_TO_EDGE));
        const fb = gl.createFramebuffer()!;
        gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
        gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
        if (gl.checkFramebufferStatus(gl.FRAMEBUFFER) !== gl.FRAMEBUFFER_COMPLETE) {
          gl.deleteTexture(tex); gl.deleteFramebuffer(fb);
          targets.forEach((t) => { gl.deleteTexture(t.tex); gl.deleteFramebuffer(t.fb); });
          targets.length = 0;
          if (texType !== gl.UNSIGNED_BYTE) { texType = gl.UNSIGNED_BYTE; return makeTargets(); }
          failRef.current?.();
          return false;
        }
        gl.clearColor(0, 0, 0, 1); gl.clear(gl.COLOR_BUFFER_BIT);
        targets.push({ tex, fb });
      }
      return true;
    };

    const imgTex = (img: HTMLImageElement) => {
      const t = gl.createTexture()!;
      gl.bindTexture(gl.TEXTURE_2D, t);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img);
      [gl.TEXTURE_MIN_FILTER, gl.TEXTURE_MAG_FILTER].forEach((p) => gl.texParameteri(gl.TEXTURE_2D, p, gl.LINEAR));
      [gl.TEXTURE_WRAP_S, gl.TEXTURE_WRAP_T].forEach((p) => gl.texParameteri(gl.TEXTURE_2D, p, gl.CLAMP_TO_EDGE));
      return t;
    };

    const txtCanvas = document.createElement("canvas");
    const txtT = gl.createTexture()!;
    const drawText = () => {
      const W = canvas.clientWidth, H = canvas.clientHeight, k = Math.min(window.devicePixelRatio || 1, 2);
      txtCanvas.width = Math.max(1, W * k); txtCanvas.height = Math.max(1, H * k);
      const c = txtCanvas.getContext("2d")!;
      c.setTransform(k, 0, 0, k, 0, 0); c.clearRect(0, 0, W, H);
      const hf = hfRef.current;
      const { left, right } = hlRef.current;
      const mobile = W < 768;
      const fs = (mobile ? W * 0.11 : Math.min(96, Math.max(40, W * 0.046))) * hf.scale;
      c.font = `${hf.weight} ${fs}px ${hf.family}`;
      (c as unknown as { letterSpacing: string }).letterSpacing = `${hf.tracking * fs}px`;
      c.fillStyle = "#000"; c.textBaseline = "top";
      const lh = fs * (mobile ? 0.9 : 0.88);
      if (mobile) {
        // one phrase per line, scaled down only if the widest phrase would overrun the 24px margins
        const lines = [...left, ...right];
        const widest = Math.max(...lines.map((l) => c.measureText(l).width));
        const fit = Math.min(1, (W - 48) / Math.max(1, widest));
        if (fit < 1) {
          c.font = `${hf.weight} ${fs * fit}px ${hf.family}`;
          (c as unknown as { letterSpacing: string }).letterSpacing = `${hf.tracking * fs * fit}px`;
        }
        // 128px top (was 96) sits the phone headline a touch lower, balanced with the head
        // a 14px pause between the two thoughts; start 7px higher so the block stays centred where it was
        const gap = 14;
        // mobile only: the block is centred on the screen (desktop keeps the split left/right framing)
        lines.forEach((l, i) => { c.textAlign = "center"; c.fillText(l, W / 2, 121 + i * lh * fit + (i >= left.length ? gap : 0)); });
      } else {
        const top = H * 0.34;
        const inset = Math.max(48, (W - 1440) / 2); // frame Damian, don't drift to the window edges
        left.forEach((l, i) => { c.textAlign = "left"; c.fillText(l, inset, top + i * lh); });
        right.forEach((l, i) => { c.textAlign = "right"; c.fillText(l, W - inset, top + i * lh); });
        if (ulRef.current && right.length) {
          const i = right.length - 1, w = c.measureText(right[i]).width, y = top + i * lh + fs * 0.98;
          c.fillRect(W - inset - w, y, w, Math.max(1.5, fs * 0.025));
        }
      }
      // logo, nav and hint: same effect system as the headline
      const cr = canvas.getBoundingClientRect();
      overlayRoot?.current?.querySelectorAll<HTMLElement>("[data-fx]").forEach((el) => {
        if (el.closest("[data-fx-off]")) return;
        const r = el.getBoundingClientRect(); const x = r.left - cr.left, y = r.top - cr.top;
        if (r.width === 0 && r.height === 0) return; // hidden at this breakpoint
        if (el.dataset.fxBox !== undefined) { c.lineWidth = 1; c.globalAlpha = 0.45; c.strokeRect(x + 0.5, y + 0.5, r.width - 1, r.height - 1); c.globalAlpha = 1; return; }
        if (el.dataset.fxLine !== undefined) { c.globalAlpha = 0.7; c.fillRect(x, y + r.height / 2, r.width, 1); c.globalAlpha = 1; return; }
        if (el instanceof HTMLImageElement) {
          if (!el.complete) { el.addEventListener("load", () => alive && drawText(), { once: true }); return; }
          c.drawImage(el, x, y, r.width, r.height); return;
        }
        const cs = getComputedStyle(el); const txt = el.dataset.fx || el.textContent || "";
        c.font = `${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
        (c as unknown as { letterSpacing: string }).letterSpacing = cs.letterSpacing === "normal" ? "0px" : cs.letterSpacing;
        c.textAlign = "left"; c.textBaseline = "middle"; c.fillText(txt, x, y + r.height / 2);
      });
      c.textBaseline = "top";
      gl.bindTexture(gl.TEXTURE_2D, txtT);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, txtCanvas);
      [gl.TEXTURE_MIN_FILTER, gl.TEXTURE_MAG_FILTER].forEach((p) => gl.texParameteri(gl.TEXTURE_2D, p, gl.LINEAR));
      [gl.TEXTURE_WRAP_S, gl.TEXTURE_WRAP_T].forEach((p) => gl.texParameteri(gl.TEXTURE_2D, p, gl.CLAMP_TO_EDGE));
    };

    let maskT: WebGLTexture | null = null;
    let realT: WebGLTexture | null = null, robotT: WebGLTexture | null = null, ready = false;
    let raf = 0, alive = true, visible = true;
    const ptr = { x: 0, y: 0, tx: 0, ty: 0, px: 0, py: 0, active: false, last: 0 };
    let phase = 0;
    let pivX = 0, pivY = 0;
    const SPK = [[0.240, 0.645], [0.771, 0.645], [0.214, 0.827], [0.797, 0.82]];
    const spkAt = [-10, -10, -10, -10]; const spkIn = [false, false, false, false];
    const eye = { x: 0, y: 0, vx: 0, vy: 0, glow: 0, hover: 0, focus: 0, locked: false, blink: 0, blinkAt: -10, nextBlink: 5, dbl: false, mouthF: 0, earF: 0, earL: 0, earR: 0, mouth: 0, side: -1, cross: 0 };
    const trail: ({ x: number; y: number; t: number } | undefined)[] = []; let trailI = 0;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    Promise.all([loadImage(realSrc), loadImage(robotSrc), loadImage(maskSrc)]).then(([a, b, mk]) => {
      if (!alive) return;
      realT = imgTex(a); robotT = imgTex(b); maskT = imgTex(mk); ready = true;
      requestAnimationFrame(() => { canvas.style.opacity = "1"; onReady?.(); });
      startPeek();
    });

    let sized = false;
    const resize = () => {
      if (!(canvas.clientWidth >= 1 && canvas.clientHeight >= 1)) { sized = false; return; } // skip until a real size returns
      const dpr = Math.min(window.devicePixelRatio || 1, lowPower ? 1.25 : 1.75);
      canvas.width = Math.round(canvas.clientWidth * dpr);
      canvas.height = Math.round(canvas.clientHeight * dpr);
      sized = makeTargets();
      drawText();
    };
    redrawRef.current = () => alive && drawText();
    resize();
    document.fonts?.ready.then(() => alive && drawText());
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);
    const io = new IntersectionObserver(([en]) => { visible = en.isIntersecting; });
    io.observe(canvas);

    const move = (cx: number, cy: number) => {
      const r = canvas.getBoundingClientRect();
      const x = (cx - r.left) / r.width, y = 1 - (cy - r.top) / r.height;
      if (!ptr.active) { ptr.px = x; ptr.py = y; ptr.x = x; ptr.y = y; ptr.active = true; }
      ptr.x = x; ptr.y = y; ptr.last = performance.now();
      { const now = performance.now(); const lq = trail[(trailI + 15) % 16];
        if (!lq || Math.hypot((x - lq.x) * (r.width / r.height), y - lq.y) > 0.012 || now - lq.t > 120) { trail[trailI % 16] = { x, y, t: now }; trailI++; } }
      fx.current.x = cx - r.left; fx.current.y = cy - r.top; fx.current.active = true;
    };
    const onMove = (e: PointerEvent) => move(e.clientX, e.clientY);
    const onLeave = () => { ptr.active = false; fx.current.active = false; };
    window.addEventListener("pointermove", onMove, { passive: true });
    document.addEventListener("pointerleave", onLeave);
    // Touch: pointer events cancel once a swipe starts scrolling, so follow raw touches too.
    const coarse = window.matchMedia("(hover: none)").matches;
    let demo: { t0: number; dur: number; cx: number; cy: number; span: number } | null = null;
    let touched = false, tStart = { x: 0, y: 0, t: 0 };
    const runDemo = (cx: number, cy: number, span: number, dur: number) => { demo = { t0: performance.now(), dur, cx, cy, span }; };
    const onTStart = (e: TouchEvent) => { const p = e.touches[0]; if (!p) return; touched = true; demo = null; tStart = { x: p.clientX, y: p.clientY, t: performance.now() }; move(p.clientX, p.clientY); };
    const onTMove = (e: TouchEvent) => { const p = e.touches[0]; if (p) move(p.clientX, p.clientY); };
    const onTEnd = (e: TouchEvent) => {
      const p = e.changedTouches[0]; onLeave();
      if (!p || performance.now() - tStart.t > 350 || Math.hypot(p.clientX - tStart.x, p.clientY - tStart.y) > 12) return;
      const r = canvas.getBoundingClientRect(); const R = rectRef.current;
      const x = p.clientX - r.left, y = p.clientY - r.top;
      if (x > R.x && x < R.x + R.w && y > R.y && y < R.y + R.h) runDemo(p.clientX, p.clientY, R.w * 0.22, 900);
    };
    window.addEventListener("touchstart", onTStart, { passive: true });
    window.addEventListener("touchmove", onTMove, { passive: true });
    window.addEventListener("touchend", onTEnd);
    // once on phones: a brief peek across the face, then the mask decays back to human
    let autoId = 0;
    function startPeek() {
      if (!coarse || reduced) return;
      // once, shortly after the portrait has faded in (canvas fade is 1.2s)
      autoId = window.setTimeout(() => {
        if (touched || !visible || !alive) return;
        const r = canvas.getBoundingClientRect(); const R = rectRef.current;
        runDemo(r.left + R.x + R.w * 0.5, r.top + R.y + R.h * 0.33, R.w * 0.2, 1800);
      }, 1100);
    }

    const U = (p: WebGLProgram, n: string) => gl.getUniformLocation(p, n);
    const start = performance.now();
    let lastT = start;
    const loop = () => {
      raf = requestAnimationFrame(loop);
      if (!visible || !sized || targets.length < 2 || gl.isContextLost()) return;
      if (demo) {
        const k = (performance.now() - demo.t0) / demo.dur;
        if (k >= 1) { demo = null; onLeave(); }
        else { const e = k < 0.5 ? 2 * k * k : 1 - Math.pow(-2 * k + 2, 2) / 2;
          move(demo.cx + (e * 2 - 1) * demo.span, demo.cy + Math.sin(k * Math.PI * 3) * demo.span * 0.45); }
      }
      const s = sRef.current;
      const now = performance.now();
      const mob = canvas.clientWidth < 768;
      const dt = !mob ? 1 : Math.min(Math.max((now - lastT) / 16.667, 0.25), 4); lastT = now; // frames-at-60 elapsed
      const E = (k: number) => 1 - Math.pow(1 - k, dt); // frame-rate independent easing
      const t = (now - start) / 1000;
      const W = canvas.clientWidth, H = canvas.clientHeight, aspect = W / Math.max(1, H);

      const dx = (ptr.x - ptr.px) * aspect, dy = ptr.y - ptr.py;
      const speed = Math.hypot(dx, dy);
      const f = fx.current;
      f.v += (Math.min(speed * 40 * s.velocity, 1) - f.v) * E(0.12);
      const rest = performance.now() - ptr.last > 3000 || !ptr.active;
      const en = (f.energy ?? 1) + (((rest ? 1 - s.quiet * 0.85 : 1)) - (f.energy ?? 1)) * E(rest ? 0.006 : 0.04);
      f.energy = en;
      const bp = (t % 4) / 4; f.thump = s.thump * (mob ? 0.5 : 1) * Math.exp(-bp * 6) * (1 - Math.exp(-bp * 60)) * (0.5 + 0.5 * en);
      const idle = performance.now() - ptr.last > 120;
      const tx = ptr.active ? ptr.x * 2 - 1 : 0, ty = ptr.active ? ptr.y * 2 - 1 : 0;
      f.parX += (tx * s.parallax - f.parX) * E(0.04);
      f.parY += (ty * s.parallax - f.parY) * E(0.04);
      // subtle pivot: turns toward the cursor. Phones keep the figure still (touch/peek made it bob into
      // the headline); desktop keeps only a faint forward lean so the head never rises into the text.
      const still = mob;
      pivX += ((still ? 0 : tx * 0.09 * (s.yaw ?? 1)) - pivX) * E(s.pivotEase ?? 0.06);
      pivY += ((still ? 0 : -ty * 0.025 * (s.pitch ?? 1)) - pivY) * E((s.pivotEase ?? 0.06) * 0.6);
      f.full = s.fullReveal;
      // living eyes: look toward the cursor from each eye's on-screen position
      {
        const R0 = rectRef.current;
        const ex = (R0.x + R0.w * 0.4957) / W, ey = 1 - (R0.y + R0.h * 0.2787) / H;
        let gx = 0, gy = 0, near = 0;
        if (ptr.active && !rest) {
          const ddx = (ptr.x - ex) * aspect, ddy = ptr.y - ey; const L = Math.hypot(ddx, ddy) || 1e-4;
          const k = Math.min(L / 0.35, 1); gx = (ddx / L) * k; gy = (ddy / L) * k; near = Math.max(0, 1 - L / 0.25);
          if (L < 0.06) eye.hover += dt / 60; else eye.hover = 0;
        } else { gx = Math.sin(t * 0.23) * 0.5 + Math.sin(t * 0.61) * 0.2; gy = Math.sin(t * 0.17 + 1) * 0.3; eye.hover = 0; }
        if (reduced) { gx = 0; gy = 0; }
        // damped spring: eyes overshoot slightly and settle, like a mechanical lens
        for (let n = Math.max(1, Math.round(dt)); n > 0; n--) { eye.vx = (eye.vx + (gx - eye.x) * 0.02) * 0.8; eye.vy = (eye.vy + (gy - eye.y) * 0.02) * 0.8; eye.x += eye.vx; eye.y += eye.vy; }
        eye.glow += (near - eye.glow) * E(0.05);
        if (eye.hover > 0.6 && !eye.locked) { eye.locked = true; eye.focus = 1; eye.mouthF = 1; eye.earF = 1; }
        if (eye.hover === 0) eye.locked = false;
        eye.focus *= Math.pow(0.97, dt); eye.mouthF *= Math.pow(0.95, dt); eye.earF *= Math.pow(0.975, dt);
        if (t > eye.nextBlink && !reduced) { eye.blinkAt = t; eye.dbl = Math.random() < 0.3; eye.nextBlink = t + (6 + Math.random() * 3) / Math.max(0.05, s.blinkRate ?? 1); }
        const bt = (t - eye.blinkAt) / 0.4;
        eye.blink = bt >= 0 && bt < 1 ? Math.sin(bt * Math.PI) : eye.dbl && bt >= 1.25 && bt < 2.25 ? Math.sin((bt - 1.25) * Math.PI) : 0;
        // headphones + mouth proximity
        const near2 = (px: number, py: number, rx: number, ry: number) => {
          if (!ptr.active || rest) return 0;
          const sx = (R0.x + R0.w * px) / W, sy = 1 - (R0.y + R0.h * py) / H;
          const qx = (ptr.x - sx) * W / (R0.w * rx), qy = (ptr.y - sy) * H / (R0.h * ry);
          return Math.max(0, 1 - Math.hypot(qx, qy) / 1.6);
        };
        eye.earL += (Math.max(near2(0.357, 0.302, 0.04, 0.085), eye.earF * 0.8) - eye.earL) * E(0.04);
        eye.earR += (Math.max(near2(0.628, 0.297, 0.04, 0.085), eye.earF * 0.8) - eye.earR) * E(0.04);
        eye.mouth += (Math.max(near2(0.495, 0.411, 0.07, 0.05), eye.mouthF) - eye.mouth) * E(0.08);
        if (reduced) { eye.earL = eye.earR = eye.mouth = 0; }
        for (let k = 0; k < 4; k++) {
          const [px, py] = SPK[k]; const on = near2(px, py, 0.05, 0.06) > 0.35;
          if (on && !spkIn[k] && !reduced) spkAt[k] = t;
          spkIn[k] = on;
        }
      }

      gl.useProgram(sim);
      const [src, dst] = targets;
      gl.bindFramebuffer(gl.FRAMEBUFFER, dst.fb);
      gl.viewport(0, 0, SW, SH);
      gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, src.tex);
      gl.uniform1i(U(sim, "prev"), 0);
      gl.uniform2f(U(sim, "a"), ptr.px, ptr.py);
      gl.uniform2f(U(sim, "b"), ptr.x, ptr.y);
      gl.uniform2f(U(sim, "vel"), ptr.x - ptr.px, ptr.y - ptr.py);
      gl.uniform1f(U(sim, "r"), (0.022 + f.v * 0.028) * s.size);
      gl.uniform1f(U(sim, "phase"), phase);
      gl.uniform1f(U(sim, "freq"), s.freq);
      gl.uniform1f(U(sim, "pinch"), s.pinch);
      gl.uniform1f(U(sim, "edgeAmp"), s.edgeAmp * (0.7 + f.v * 0.8));
      gl.uniform1f(U(sim, "edgeMove"), s.edgeMove);
      phase += speed * s.freq;
      gl.uniform1f(U(sim, "amt"), ptr.active && !idle ? (0.05 + f.v * 0.3) * s.strength : 0);
      gl.uniform1f(U(sim, "decay"), s.decay);
      gl.uniform1f(U(sim, "visc"), s.viscosity);
      gl.uniform1f(U(sim, "t"), t);
      gl.uniform1f(U(sim, "aspect"), aspect);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      targets.reverse();
      ptr.px = ptr.x; ptr.py = ptr.y;

      if (!ready) return;
      const R = rectRef.current;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, canvas.width, canvas.height);
      gl.useProgram(comp);
      gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, realT);
      gl.activeTexture(gl.TEXTURE1); gl.bindTexture(gl.TEXTURE_2D, robotT);
      gl.activeTexture(gl.TEXTURE2); gl.bindTexture(gl.TEXTURE_2D, targets[0].tex);
      gl.activeTexture(gl.TEXTURE3); gl.bindTexture(gl.TEXTURE_2D, txtT);
      gl.uniform1i(U(comp, "txt"), 3);
      gl.uniform1f(U(comp, "glassOn"), s.glass ? 1 : 0);
      gl.uniform1f(U(comp, "refr"), s.refraction);
      gl.activeTexture(gl.TEXTURE4); gl.bindTexture(gl.TEXTURE_2D, maskT);
      gl.uniform1i(U(comp, "smask"), 4);
      gl.uniform1f(U(comp, "split"), s.split);
      gl.uniform1f(U(comp, "scanD"), s.scan);
      gl.uniform1f(U(comp, "placeholder"), phRef.current ? 1 : 0);
      gl.uniform1i(U(comp, "real"), 0);
      gl.uniform1i(U(comp, "robot"), 1);
      gl.uniform1i(U(comp, "mask"), 2);
      gl.uniform1f(U(comp, "t"), t);
      gl.uniform1f(U(comp, "full"), s.fullReveal);
      gl.uniform1f(U(comp, "mixv"), s.mix);
      gl.uniform1i(U(comp, "mode"), ["normal", "opacity", "split", "overlay", "mask"].indexOf(s.debug));
      // rect in uv space, bottom-left origin
      gl.uniform4f(U(comp, "rect"), R.x / W, 1 - (R.y + R.h) / H, R.w / W, R.h / H);
      gl.uniform2f(U(comp, "par"), f.parX, f.parY);
      gl.uniform2f(U(comp, "piv"), pivX, pivY);
      gl.uniform1f(U(comp, "seamX"), f.seam ?? 1.2);
      gl.uniform1f(U(comp, "seamJag"), s.seamJag ?? 1); gl.uniform1f(U(comp, "mob"), mob ? 1 : 0);
      gl.uniform1f(U(comp, "seamY"), f.seamY ?? 0);
      gl.uniform1f(U(comp, "gStreams"), s.gStreams ?? 1);gl.uniform1f(U(comp, "gSpeed"), s.gSpeed ?? 1);gl.uniform1f(U(comp, "gCurve"), s.gCurve ?? 1);
      gl.uniform1f(U(comp, "liveR"), s.glass ? 1 : 0);
      { const side = ptr.x > (f.seam ?? 1.2) ? 1 : 0; if (eye.side !== side && ptr.active) eye.cross = 1; eye.side = side; eye.cross *= 0.94;
        gl.uniform1f(U(comp, "seamGlow"), (s.seamGlow ?? 1) * (1 + eye.cross * 1.2)); }
      gl.uniform1f(U(comp, "aspect"), aspect);
      gl.uniform1f(U(comp, "waves"), s.waves);
      gl.uniform1f(U(comp, "cap"), s.cap);
      gl.uniform1f(U(comp, "envBlend"), s.envBlend);
      gl.uniform1f(U(comp, "xray"), s.xray);
      gl.uniform1f(U(comp, "xrayRate"), s.xrayRate);
      gl.uniform1f(U(comp, "trailW"), s.trailWaves);
      gl.uniform1f(U(comp, "bgPulse"), s.bgPulse * (0.35 + 0.65 * (f.energy ?? 1)) * (1 + (f.thump ?? 0) * 0.6));
      gl.uniform2f(U(comp, "lp"), ptr.x ?? 0.5, ptr.y ?? 0.6);
      gl.uniform1f(U(comp, "thumpV"), f.thump ?? 0); gl.uniform1f(U(comp, "energy"), f.energy ?? 1);
      gl.uniform1f(U(comp, "breathV"), s.breath * (mob ? 0.002 : 0.003) * (0.5 + 0.5 * Math.sin(t * Math.PI / (mob ? 4.5 : 3))));
      gl.uniform1f(U(comp, "shadowOn"), s.shadow); gl.uniform1f(U(comp, "grainOn"), s.grain); gl.uniform1f(U(comp, "vigOn"), s.vignette);
      { const fo = s.eyeFollow ?? 1; gl.uniform2f(U(comp, "eyeOff"), eye.x * 0.0055 * fo, -eye.y * 0.004 * fo); }
      gl.uniform1f(U(comp, "earL"), eye.earL); gl.uniform1f(U(comp, "earR"), eye.earR); gl.uniform1f(U(comp, "mouthV"), eye.mouth);
      gl.uniform1f(U(comp, "earOn"), s.earGlow ?? 1); gl.uniform1f(U(comp, "mouthOn"), s.mouthMeter ?? 1);
      gl.uniform1f(U(comp, "inkOn"), s.ink ?? 1); gl.uniform1f(U(comp, "lampOn"), s.lamp ?? 1); gl.uniform1f(U(comp, "breatheOn"), reduced ? 0 : s.humanBreathe ?? 1);
      gl.uniform1f(U(comp, "ptrOn"), ptr.active ? 1 : 0);
      gl.uniform1f(U(comp, "greenOn"), s.accent === "green" || s.accent === "emerald" ? 1 : 0);
      gl.uniform1f(U(comp, "rlOn"), s.robotLines === false ? 0 : 1); gl.uniform1f(U(comp, "rlSpeed"), s.rlSpeed ?? 1);
      gl.uniform1f(U(comp, "rlPulse"), reduced ? 0 : s.rlPulse ?? 0.6); gl.uniform1f(U(comp, "rlBreath"), reduced ? 0 : s.rlBreath ?? 0.6);
      gl.uniform1f(U(comp, "rlWake"), s.rlWake ?? 0.6);
      gl.uniform1f(U(comp, "rlRange"), s.rlRange ?? 1); gl.uniform1f(U(comp, "rlGlow"), s.rlGlow ?? 1); gl.uniform1f(U(comp, "rlCur"), s.rlCurrents ?? 3);
      gl.uniform1f(U(comp, "rlNeon"), s.rlNeon ?? 1.4); gl.uniform1f(U(comp, "rlBloom"), s.rlBloom ?? 1); gl.uniform1f(U(comp, "rlCon"), s.rlContrast ?? 0.6);
      gl.uniform1f(U(comp, "cuOn"), s.copper === false || reduced ? 0 : 1); gl.uniform1f(U(comp, "cuAmt"), s.cuAmt ?? 1);
      gl.uniform1f(U(comp, "cuSpeed"), s.cuSpeed ?? 1); gl.uniform1f(U(comp, "cuHand"), s.cuHand ?? 0.8);
      gl.uniform1f(U(comp, "spAmt"), s.spkAmt ?? 1); gl.uniform1f(U(comp, "spR"), s.spkSize ?? 1);
      { const sa: number[] = []; for (let k = 0; k < 4; k++) sa.push(SPK[k][0], SPK[k][1], t - spkAt[k]); gl.uniform3fv(U(comp, "spk[0]"), sa); }
      if (s.accent === "emerald") gl.uniform3f(U(comp, "accentCol"), 0.086, 0.639, 0.29); else gl.uniform3f(U(comp, "accentCol"), 0.114, 0.725, 0.329);
      gl.uniform1f(U(comp, "pencilOn"), s.pencil ?? 1); gl.uniform1f(U(comp, "dustOn"), s.dust ?? 1);
      { const now = performance.now(); const arr = new Float32Array(48);
        for (let i = 0; i < 16; i++) { const q = trail[(trailI - 1 - i + 64) % 16]; if (!q) continue; const age = (now - q.t) / 3500; arr[i * 3] = q.x; arr[i * 3 + 1] = q.y; arr[i * 3 + 2] = Math.max(0, 1 - age); }
        gl.uniform3fv(U(comp, "pts[0]"), arr); }
      { const R1 = rectRef.current; gl.uniform2f(U(comp, "irisC"), (R1.x + R1.w * 0.5446) / W, 1 - (R1.y + R1.h * 0.2787) / H); gl.uniform1f(U(comp, "irisP"), f.iris ?? 0); }
      gl.uniform1f(U(comp, "eyeGlowV"), (0.35 + eye.glow * 0.9) * (s.eyeGlow ?? 1));
      gl.uniform1f(U(comp, "focusP"), eye.focus); gl.uniform1f(U(comp, "blinkV"), eye.blink);
      gl.uniform1f(U(comp, "sweepOn"), reduced ? 0 : s.sweep ?? 1); gl.uniform1f(U(comp, "reflOn"), s.reflect ?? 1);
      gl.uniform1f(U(comp, "lightOn"), s.light); gl.uniform1f(U(comp, "softOn"), s.soft); gl.uniform1f(U(comp, "chestOn"), s.chest);
      gl.uniform1f(U(comp, "dSpec"), s.dSpec); gl.uniform1f(U(comp, "dRib"), s.dRib); gl.uniform1f(U(comp, "dVinyl"), s.dVinyl);
      gl.uniform1f(U(comp, "dBloom"), s.dBloom); gl.uniform1f(U(comp, "dDust"), s.dDust);
      gl.uniform1f(U(comp, "envReact"), s.envReact);
      gl.uniform1f(U(comp, "showEnv"), s.showEnv ? 1 : 0);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    };
    loop();

    return () => {
      canvas.removeEventListener("webglcontextlost", onLost);
      canvas.removeEventListener("webglcontextrestored", onRestored);
      alive = false;
      cancelAnimationFrame(raf);
      ro.disconnect(); io.disconnect();
      window.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerleave", onLeave);
      window.removeEventListener("touchstart", onTStart);
      window.removeEventListener("touchmove", onTMove);
      window.removeEventListener("touchend", onTEnd);
      window.clearTimeout(autoId);
      gl.getExtension("WEBGL_lose_context")?.loseContext();
    };
  }, [realSrc, robotSrc, maskSrc, lowPower, fx, onReady, glGen]);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 h-full w-full"
      style={{ opacity: 0, transition: "opacity 1200ms cubic-bezier(.2,.7,.2,1)" }}
      aria-label="Portrait of Damian Keyes; moving or swiping across it reveals the engineered machine underneath"
      role="img"
    />
  );
};

export default FluidReveal;
